import { resolveLocalizedText } from './localizedText';
import type { AttributeDefinition, ProductAttributeSearchFilter } from '../model/types';

/** Loosely-typed to match react-i18next's `t` without dragging in the full `TFunction` generics (mirrors `ProductPublicationCard`). */
type Translate = (key: string, vars?: Record<string, unknown>) => string;

export function attributeFilterLabel(
  code: string,
  dictionary: AttributeDefinition[],
  locale: string,
): string {
  const def = dictionary.find((d) => d.code === code);
  return def ? resolveLocalizedText(def.translations, 'label', locale, def.code) : code;
}

/**
 * `from`/`to` on `ProductAttributeSearchFilter` are numbers; for a DATE attribute they carry epoch
 * milliseconds (see `AttributeFiltersPopover`'s doc comment on that encoding choice) — rendered
 * back to a plain date for display.
 */
function epochMillisToDate(value: number | undefined): string {
  if (value === undefined) return '';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '' : date.toISOString().slice(0, 10);
}

/** Human-readable summary of one active attribute filter, for a chip or popover row. */
export function describeAttributeFilter(
  filter: ProductAttributeSearchFilter,
  dictionary: AttributeDefinition[],
  locale: string,
  t: Translate,
): string {
  const def = dictionary.find((d) => d.code === filter.code);
  if (filter.value !== undefined) {
    if (def?.type === 'SELECT') {
      const option = def.options?.find((o) => o.code === filter.value);
      return option
        ? resolveLocalizedText(option.translations, 'label', locale, String(filter.value))
        : String(filter.value);
    }
    if (def?.type === 'BOOLEAN') {
      return filter.value ? t('productAttributesPanel.booleanYes') : t('productAttributesPanel.booleanNo');
    }
    return String(filter.value);
  }
  const from = def?.type === 'DATE' ? epochMillisToDate(filter.from) : (filter.from?.toString() ?? '');
  const to = def?.type === 'DATE' ? epochMillisToDate(filter.to) : (filter.to?.toString() ?? '');
  return [from, to].filter(Boolean).join(' – ');
}
